import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  Calendar, 
  Building2, 
  Activity,
  TrendingUp,
  Clock,
  BarChart3
} from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatDateBR } from '../utils/dateUtils';

export default function DashboardPage() {
  const { hasFullAccess, isAdminGeral, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  const ROLE_LEVELS = { master: 5, admin_geral: 4, admin: 3, marcador_geral: 2, marcador: 1 };
  const isSecretaria = user?.is_secretaria === true;
  const isAdminGeralPlus = (ROLE_LEVELS[user?.role] || 0) >= ROLE_LEVELS.admin_geral || isSecretaria;

  const getPeriodDates = (p) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    const dow = now.getDay();
    const pad = (n) => String(n).padStart(2, '0');
    
    switch (p) {
      case 'day':
        const d = `${year}-${pad(month + 1)}-${pad(day)}`;
        return { data_inicio: d, data_fim: d };
      case 'week': {
        const start = new Date(now);
        start.setDate(day - dow);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        return {
          data_inicio: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
          data_fim: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`
        };
      }
      case 'month': {
        const lastDay = new Date(year, month + 1, 0).getDate();
        return { data_inicio: `${year}-${pad(month + 1)}-01`, data_fim: `${year}-${pad(month + 1)}-${pad(lastDay)}` };
      }
      case 'semester': {
        const semStart = month < 6 ? 0 : 6;
        const semEnd = month < 6 ? 5 : 11;
        const lastDaySem = new Date(year, semEnd + 1, 0).getDate();
        return { data_inicio: `${year}-${pad(semStart + 1)}-01`, data_fim: `${year}-${pad(semEnd + 1)}-${pad(lastDaySem)}` };
      }
      case 'year':
        return { data_inicio: `${year}-01-01`, data_fim: `${year}-12-31` };
      default:
        return {};
    }
  };

  useEffect(() => {
    fetchStats();
  }, [period]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const dates = getPeriodDates(period);
      const params = new URLSearchParams();
      if (dates.data_inicio) params.append('data_inicio', dates.data_inicio);
      if (dates.data_fim) params.append('data_fim', dates.data_fim);
      const response = await api.get(`/dashboard/stats?${params.toString()}`);
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando estatísticas...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total de Pacientes',
      value: stats?.total_patients || 0,
      icon: Users,
      color: 'teal',
      bgColor: 'bg-teal-50',
      iconColor: 'text-teal-600',
      borderColor: 'border-l-teal-500',
      show: true
    },
    {
      title: 'Consultas Marcadas',
      value: stats?.appointments_by_status?.Marcada || 0,
      icon: Calendar,
      color: 'sky',
      bgColor: 'bg-sky-50',
      iconColor: 'text-sky-600',
      borderColor: 'border-l-sky-500',
      show: true
    },
    {
      title: 'Unidades de Saúde',
      value: stats?.total_units || 0,
      icon: Building2,
      color: 'emerald',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      borderColor: 'border-l-emerald-500',
      show: isAdminGeralPlus
    },
    {
      title: 'Usuários Ativos',
      value: stats?.total_users || 0,
      icon: Activity,
      color: 'violet',
      bgColor: 'bg-violet-50',
      iconColor: 'text-violet-600',
      borderColor: 'border-l-violet-500',
      show: isAdminGeralPlus
    },
  ].filter(card => card.show);

  const appointmentTypeData = stats?.appointments_by_type ? [
    { name: 'Consultas', value: stats.appointments_by_type['Consulta'] || 0 },
    { name: 'Exames', value: stats.appointments_by_type['Exame'] || 0 },
  ] : [];

  const appointmentStatusData = stats?.appointments_by_status ? [
    { name: 'Marcada', value: stats.appointments_by_status['Marcada'] || 0 },
    { name: 'Realizada', value: stats.appointments_by_status['Realizada'] || 0 },
    { name: 'Cancelada', value: stats.appointments_by_status['Cancelada'] || 0 },
  ] : [];

  const COLORS = ['#0f766e', '#0ea5e9', '#10b981', '#f43f5e'];
  const UNIT_COLORS = ['#0f766e', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16', '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#a855f7'];

  const slotsUsageData = (stats?.slots_usage_by_unit || []).map(item => ({
    ...item,
    name: item.unit_name.length > 20 ? item.unit_name.substring(0, 20) + '...' : item.unit_name,
    fullName: item.unit_name
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white px-4 py-3 rounded-lg border border-slate-200 shadow-lg">
          <p className="font-semibold text-slate-900 text-sm">{data.fullName}</p>
          <p className="text-teal-700 text-sm mt-1">Vagas Ocupadas: <span className="font-bold">{data.occupied}</span></p>
          <p className="text-slate-600 text-sm">Percentual: <span className="font-bold">{data.percentage}%</span></p>
        </div>
      );
    }
    return null;
  };

  const periodLabels = { day: 'Hoje', week: 'Semana', month: 'Mês', semester: 'Semestre', year: 'Ano' };

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">Dashboard</h1>
          <p className="text-slate-600">Visão geral do sistema de gestão de consultas</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-sm" data-testid="period-filter">
          {['day', 'week', 'month', 'semester', 'year'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                period === p
                  ? 'bg-teal-700 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`period-${p}`}
            >
              {periodLabels[p]}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className={`p-6 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow border-l-4 ${stat.borderColor}`}
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-6 w-6 ${stat.iconColor}`} strokeWidth={2} />
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-1">
                  {stat.title}
                </p>
                <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Section - Apenas Admin Geral ou superior */}
      {isAdminGeralPlus && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Appointments by Type */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-teal-600" />
            Distribuição por Tipo
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={appointmentTypeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {appointmentTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Appointments by Status */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-teal-600" />
            Status das Consultas
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={appointmentStatusData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }} 
              />
              <Bar dataKey="value" fill="#0f766e" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}

      {/* Slot Usage by Unit Chart */}
      {(ROLE_LEVELS[user?.role] || 0) >= ROLE_LEVELS.admin_geral && slotsUsageData.length > 0 && (
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm mb-8" data-testid="slots-usage-chart">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-teal-600" />
            Percentual de Vagas Utilizadas por Unidade
          </h3>
          <ResponsiveContainer width="100%" height={Math.max(300, slotsUsageData.length * 45)}>
            <BarChart data={slotsUsageData} layout="vertical" margin={{ left: 20, right: 40, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} stroke="#64748b" />
              <YAxis type="category" dataKey="name" width={180} stroke="#64748b" tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="percentage" radius={[0, 8, 8, 0]} barSize={28}>
                {slotsUsageData.map((entry, index) => (
                  <Cell key={`cell-unit-${index}`} fill={UNIT_COLORS[index % UNIT_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 flex flex-wrap gap-4">
            {slotsUsageData.map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: UNIT_COLORS[index % UNIT_COLORS.length] }}></div>
                <span className="text-slate-600">{item.fullName}: <span className="font-bold text-slate-900">{item.percentage}%</span> ({item.occupied} vagas)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Appointments */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Clock className="h-5 w-5 text-teal-600" />
            Consultas Recentes
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-semibold text-xs h-12">
              <tr>
                <th className="px-6 py-3">Paciente</th>
                <th className="px-6 py-3">Tipo</th>
                <th className="px-6 py-3">Data</th>
                <th className="px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recent_appointments && stats.recent_appointments.length > 0 ? (
                stats.recent_appointments.map((appointment) => (
                  <tr
                    key={appointment.id}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors h-14"
                  >
                    <td className="px-6 py-3 font-medium text-slate-900">
                      {appointment.patient_name}
                    </td>
                    <td className="px-6 py-3 text-slate-600">{appointment.type}</td>
                    <td className="px-6 py-3 text-slate-600 font-mono text-xs">
                      {formatDateBR(appointment.date)}
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={
                          `inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            appointment.status === 'Marcada'
                              ? 'bg-sky-50 text-sky-700 border border-sky-200'
                              : appointment.status === 'Realizada'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-rose-50 text-rose-700 border border-rose-200'
                          }`
                        }
                      >
                        {appointment.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-slate-500">
                    Nenhuma consulta encontrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}